import { useNavigate } from "@tanstack/react-router";

import { CurrentPlanCard } from "./components/CurrentPlanCard";
import { OverviewSkeleton } from "./components/OverviewSkeleton";
import { PaymentMethodsCard } from "./components/PaymentMethodsCard";
import { RecentInvoicesSection } from "./components/RecentInvoicesSection";
import { UpcomingPaymentCard } from "./components/UpcomingPaymentCard";
import { useSubscriptionOverview } from "./hooks/useSubscriptionOverview";

interface OverviewTabProps {
  readonly projectId?: string;
}

function extractProjectIdFromUrl(): number {
  const match = window.location.pathname.match(/\/billing\/(\d+)/);

  return match ? Number(match[1]) : 0;
}

export function OverviewTab({ projectId: propProjectId }: OverviewTabProps) {
  const projectId = propProjectId
    ? Number(propProjectId)
    : extractProjectIdFromUrl();
  const { data, isError, isPending, refetch } =
    useSubscriptionOverview(projectId);
  const navigate = useNavigate();

  if (isPending) {
    return <OverviewSkeleton />;
  }

  if (isError) {
    return (
      <div className="p-xl">
        <p className="text-sm text-text-secondary">
          Failed to load subscription overview.
        </p>
        <button
          className="mt-md text-sm text-button-primary-bg hover:underline"
          onClick={() => refetch()}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-xl flex flex-col gap-xl sm:gap-3xl">
      <div className="grid grid-cols-1 gap-md md:gap-xl md:grid-cols-3">
        <CurrentPlanCard data={data} />

        <PaymentMethodsCard
          data={data}
          onNavigate={() =>
            navigate({
              params: { projectId: String(projectId) },
              to: "/billing/$projectId/payment-methods",
            })
          }
        />

        <UpcomingPaymentCard data={data} />
      </div>

      <RecentInvoicesSection
        onSeeAll={() =>
          navigate({
            params: { projectId: String(projectId) },
            to: "/billing/$projectId/invoices",
          })
        }
      />
    </div>
  );
}
